import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  gql,
  ApolloProvider
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';

// Environment variables
const REALM_APP_ID = process.env.REACT_APP_REALM_APP_ID;
const GRAPHQL_ENDPOINT = `https://eu-central-1.aws.services.cloud.mongodb.com/api/client/v2.0/app/${REALM_APP_ID}/graphql`;
const NEW_GRAPHQL_ENDPOINT = process.env.REACT_APP_NEW_GRAPHQL_ENDPOINT;
const USE_NEW_GRAPHQL_SERVER =
  process.env.REACT_APP_USE_NEW_GRAPHQL_SERVER === 'true';
const AUTHENTICATED_ENDPOINT =
  process.env.REACT_APP_USE_GRAPHQL_SERVER_AUTHENTICATED;
const UNAUTHENTICATED_ENDPOINT =
  process.env.REACT_APP_USE_GRAPHQL_SERVER_NOT_AUTHENTICATED;

// Create context
export const MobileAuthContext = createContext();

// Custom hook to use the auth context
export const useMobileAuth = () => {
  const context = useContext(MobileAuthContext);
  if (!context) {
    throw new Error('useMobileAuth must be used within a MobileAuthProvider');
  }
  return context;
};

// Mobile WebView Auth Provider - Compatible with existing auth interface
export const MobileAuthProvider = ({ children }) => {
  // State management
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [sessionData, setSessionData] = useState(null);
  const [apolloClient, setApolloClient] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [error, setError] = useState(null);
  const [currentClientType, setCurrentClientType] = useState(
    USE_NEW_GRAPHQL_SERVER ? 'direct' : 'realm'
  );
  const [isAuthenticationPending, setIsAuthenticationPending] = useState(true);
  const [authTimeout, setAuthTimeout] = useState(null);

  // Computed properties for compatibility
  const isAnonymous = !isAuthenticated;
  const hasOrganizerAccess =
    userGroups.includes('Organizers') || userGroups.includes('SuperAdmins');
  const isWebView = true; // Always true for this provider

  // Initialize Apollo Client based on environment
  const initializeApolloClient = (tokens) => {
    try {
      console.log('🔧 Creating Apollo Client for mobile auth');

      let client;

      if (USE_NEW_GRAPHQL_SERVER && NEW_GRAPHQL_ENDPOINT) {
        console.log(
          '🚀 Creating direct Cognito Apollo client for mobile WebView'
        );
        client = createDirectCognitoApolloClient(tokens);
        setCurrentClientType('direct');
      } else {
        console.log('🚀 Creating Realm-based Apollo client for mobile WebView');
        client = createRealmApolloClient(tokens);
        setCurrentClientType('realm');
      }

      setApolloClient(client);
      console.log('✅ Apollo Client created successfully');
    } catch (error) {
      console.error('❌ Failed to create Apollo Client:', error);
      setError(error.message);
    }
  };

  // Helper function to get the appropriate GraphQL endpoint
  const getGraphQLEndpoint = (isAuthenticated = true) => {
    // Only use different endpoints if new GraphQL server is enabled
    if (
      USE_NEW_GRAPHQL_SERVER &&
      AUTHENTICATED_ENDPOINT &&
      UNAUTHENTICATED_ENDPOINT
    ) {
      const endpoint = isAuthenticated
        ? AUTHENTICATED_ENDPOINT
        : UNAUTHENTICATED_ENDPOINT;
      console.log(
        `🎯 Using ${
          isAuthenticated ? 'authenticated' : 'unauthenticated'
        } endpoint: ${endpoint}`
      );
      return endpoint;
    }

    // Fallback to default endpoints
    const endpoint = USE_NEW_GRAPHQL_SERVER
      ? NEW_GRAPHQL_ENDPOINT
      : GRAPHQL_ENDPOINT;
    console.log(`🎯 Using default endpoint: ${endpoint}`);
    return endpoint;
  };

  // Create direct Cognito Apollo client (new method)
  const createDirectCognitoApolloClient = (tokens) => {
    // For DirectCognito, we assume authenticated requests since we have tokens
    const endpoint = getGraphQLEndpoint(true);
    const httpLink = new HttpLink({
      uri: endpoint
    });

    const authLink = setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          authorization: tokens.idToken ? `Bearer ${tokens.idToken}` : ''
        }
      };
    });

    return new ApolloClient({
      link: from([authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network'
        },
        query: {
          fetchPolicy: 'cache-first'
        }
      }
    });
  };

  // Create Realm-based Apollo client (traditional method)
  const createRealmApolloClient = (tokens) => {
    // For Realm-based client, determine authentication status based on tokens
    const isAuthenticated = tokens && (tokens.accessToken || tokens.idToken);
    const endpoint = getGraphQLEndpoint(isAuthenticated);
    const httpLink = new HttpLink({
      uri: endpoint
    });

    const authLink = setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          authorization: tokens.accessToken
            ? `Bearer ${tokens.accessToken}`
            : ''
        }
      };
    });

    return new ApolloClient({
      link: from([authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network'
        },
        query: {
          fetchPolicy: 'cache-first'
        }
      }
    });
  };

  // Clear authentication timeout
  const clearAuthTimeout = () => {
    if (authTimeout) {
      clearTimeout(authTimeout);
      setAuthTimeout(null);
    }
  };

  // Handle authentication timeout
  const handleAuthTimeout = () => {
    console.warn('⏰ Mobile authentication timeout after 10 seconds');
    setError(
      'Authentication timeout. Mobile app did not provide credentials within 10 seconds.'
    );
    setIsAuthenticationPending(false);
    setIsLoading(false);
    clearAuthTimeout();
  };

  // Request authentication retry
  const retryAuthentication = () => {
    console.log('🔄 Retrying mobile authentication');
    setError(null);
    setIsAuthenticationPending(true);
    setIsLoading(true);

    // Set up new timeout
    const timeoutId = setTimeout(handleAuthTimeout, 10000);
    setAuthTimeout(timeoutId);

    // Request auth from mobile app
    if (window.requestMobileAuth) {
      window.requestMobileAuth();
    } else {
      try {
        const dispatchFn =
          window.__originalDispatchEvent || window.dispatchEvent;
        dispatchFn.call(window, new Event('requestMobileAuth'));
      } catch (error) {
        console.error('Failed to request mobile auth:', error);
      }
    }
  };

  // Process mobile authentication
  const processMobileAuth = async (mobileSessionData) => {
    console.log('📱 Processing mobile authentication in MobileAuthProvider');

    // Clear any existing timeout
    clearAuthTimeout();

    setIsLoading(true);
    setError(null);

    try {
      // Validate session data
      if (
        !mobileSessionData ||
        !mobileSessionData.idToken ||
        !mobileSessionData.accessToken
      ) {
        throw new Error('Invalid session data received');
      }

      // Store session data
      setSessionData(mobileSessionData);

      // Extract user information from token payload
      const payload = mobileSessionData.idTokenPayload;
      if (payload) {
        setUserId(payload.sub || payload['custom:userId']);

        // Extract groups
        const groups = payload['custom:groups']
          ? payload['custom:groups'].split(',')
          : [];
        setUserGroups(groups);

        console.log('👤 User authenticated:', {
          userId: payload.sub,
          email: payload.email,
          groups: groups
        });
      }

      // Initialize Apollo Client with tokens
      initializeApolloClient(mobileSessionData);

      // Set authenticated state
      setIsAuthenticated(true);
      setIsAuthenticationPending(false);

      // Expose token for WebView access in restApi.js
      if (mobileSessionData.idToken) {
        window.__authIdToken = mobileSessionData.idToken;
        console.log('📱 Exposed idToken to window for WebView access');
      }

      console.log('✅ Mobile authentication completed successfully');
    } catch (error) {
      console.error('❌ Mobile authentication failed:', error);
      setError(error.message);
      setIsAuthenticated(false);
      setIsAuthenticationPending(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    console.log('📱 Logging out from mobile auth');
    setIsAuthenticated(false);
    setUserId(null);
    setUserGroups([]);
    setSessionData(null);
    setApolloClient(null);
    setOrganizations([]);
    setError(null);

    // Clear exposed token
    window.__authIdToken = null;
  };

  // Login function (for compatibility)
  const login = async (credentials) => {
    console.log('📱 Login called - redirecting to mobile auth');
    // In mobile context, we rely on processMobileAuth instead
    return false;
  };

  // Refresh session (for compatibility)
  const refreshSession = async () => {
    console.log(
      '📱 Refresh session called - mobile tokens should be managed by React Native'
    );
    return sessionData;
  };

  // Refetch auth data (for compatibility)
  const refetchAuthData = async () => {
    console.log('📱 Refetch auth data called');
    return sessionData;
  };

  // Debug helpers for mobile
  const debugWebViewAuth = {
    getAuthState: () => ({
      isAuthenticated,
      userId,
      userGroups,
      sessionData: sessionData
        ? {
            hasIdToken: !!sessionData.idToken,
            hasAccessToken: !!sessionData.accessToken,
            expiresAt: sessionData.expiresAt
          }
        : null
    }),
    getTokenState: () => sessionData,
    testApiConnectivity: async () => {
      if (!apolloClient) {
        throw new Error('Apollo Client not available');
      }
      // Simple test query
      return apolloClient.query({
        query: gql`
          query {
            __typename
          }
        `,
        fetchPolicy: 'no-cache'
      });
    },
    requestMobileAuth: () => {
      console.log('📱 Requesting mobile auth');
      if (window.requestMobileAuth) {
        window.requestMobileAuth();
      } else {
        console.warn('requestMobileAuth function not available');
      }
    }
  };

  // Initialize mobile auth handlers
  useEffect(() => {
    console.log('📱 MobileAuthProvider initialized');

    // Global handler for mobile auth
    window.handleMobileAuth = async (data) => {
      console.log('📱 Global handleMobileAuth called in MobileAuthProvider');
      await processMobileAuth(data);
    };

    // Listen for mobile auth events
    const handleMobileAuthReady = (event) => {
      console.log('📡 mobileAuthReady event received in MobileAuthProvider');
      processMobileAuth(event.detail);
    };

    const handleMobileAuthError = (event) => {
      console.error('📡 mobileAuthError event received:', event.detail);
      setError(event.detail?.error || 'Mobile authentication error');
      setIsLoading(false);
    };

    const handleMobileAuthTimeout = (event) => {
      console.error('📡 mobileAuthTimeout event received:', event.detail);
      setError('Mobile authentication timeout');
      setIsLoading(false);
    };

    // Add event listeners
    window.addEventListener('mobileAuthReady', handleMobileAuthReady);
    window.addEventListener('mobileAuthError', handleMobileAuthError);
    window.addEventListener('mobileAuthTimeout', handleMobileAuthTimeout);

    // Request auth from mobile app and set up timeout
    setTimeout(() => {
      console.log('📱 Requesting initial mobile authentication');

      // Set up 10-second timeout for authentication
      const timeoutId = setTimeout(handleAuthTimeout, 10000);
      setAuthTimeout(timeoutId);

      if (window.requestMobileAuth) {
        window.requestMobileAuth();
      } else {
        // Fallback - dispatch event
        try {
          const dispatchFn =
            window.__originalDispatchEvent || window.dispatchEvent;
          dispatchFn.call(window, new Event('requestMobileAuth'));
        } catch (error) {
          console.error('Failed to request mobile auth:', error);
        }
      }
    }, 1000);

    // Cleanup
    return () => {
      window.removeEventListener('mobileAuthReady', handleMobileAuthReady);
      window.removeEventListener('mobileAuthError', handleMobileAuthError);
      window.removeEventListener('mobileAuthTimeout', handleMobileAuthTimeout);
      delete window.handleMobileAuth;
      clearAuthTimeout();
    };
  }, []);

  // Debug logging for authentication state
  React.useEffect(() => {
    console.log('🔍 MobileAuthProvider State Debug:', {
      isAuthenticated,
      isLoading,
      isAuthenticationPending,
      hasApolloClient: !!apolloClient,
      hasSessionData: !!sessionData,
      hasUserId: !!userId,
      hasIdTokenPayload: !!sessionData?.idTokenPayload,
      error
    });
  }, [
    isAuthenticated,
    isLoading,
    isAuthenticationPending,
    apolloClient,
    sessionData,
    userId,
    error
  ]);

  // Context value - compatible with existing auth provider interface
  const value = {
    // Authentication state
    isAuthenticated,
    isAnonymous,
    isLoading,
    userId,
    userGroups,
    hasOrganizerAccess,
    organizations,

    // Apollo Client
    apolloClient,

    // Session data (mobile-specific)
    sessionData,

    // Functions
    login,
    logout,
    refreshSession,
    refetchAuthData,

    // Mobile-specific
    processMobileAuth,
    isWebView,
    debugWebViewAuth,
    currentClientType,
    isAuthenticationPending,
    retryAuthentication,

    // Error state
    error,

    // Add idTokenPayload for compatibility
    idTokenPayload: sessionData?.idTokenPayload
  };

  // Don't render children until Apollo client is available
  // But allow rendering if authentication is pending but we have a client (partial success)
  if (!apolloClient && (isLoading || isAuthenticationPending)) {
    return (
      <MobileAuthContext.Provider value={value}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {error ? (
            <div>
              <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>
              <button
                onClick={retryAuthentication}
                style={{ padding: '10px 20px' }}>
                Retry Authentication
              </button>
            </div>
          ) : (
            <div>
              <div>Waiting for mobile authentication...</div>
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                This may take a few seconds
              </div>
            </div>
          )}
        </div>
      </MobileAuthContext.Provider>
    );
  }

  return (
    <MobileAuthContext.Provider value={value}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </MobileAuthContext.Provider>
  );
};

// Export for compatibility
export default MobileAuthProvider;
